import type { HostInitMessage } from './types/host-init-message'
import type { EmbedErrorCode } from './types/embed-error-code'
import type { LarryToHostMessage } from './types/larry-to-host-message'

/** `source` on every message Larry posts to its host. */
export const LARRY_EDITOR_SOURCE = 'larry-editor'

/** `source` on every message the host (Mary) posts to Larry. */
export const MARY_HOST_SOURCE = 'mary-host'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function optionalString(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()

  return trimmed === '' ? undefined : trimmed
}

/**
 * Reads the host's `init` message, or null for anything else (other frames' messages, other
 * actions, or an `init` without a token). `jobId` may arrive as a number and is normalized to a
 * string, matching the JWT's `jobId` claim.
 */
export function parseHostInitMessage(data: unknown): HostInitMessage | null {
  if (!isRecord(data) || data.source !== MARY_HOST_SOURCE || data.action !== 'init') return null
  if (!isRecord(data.payload)) return null

  const wpcauth = optionalString(data.payload.wpcauth)

  if (!wpcauth) return null

  const jobId = optionalString(data.payload.jobId)
  const userName = optionalString(data.payload.userName)

  return {
    source: MARY_HOST_SOURCE,
    action: 'init',
    payload: {
      wpcauth,
      ...(jobId ? { jobId } : {}),
      ...(userName ? { userName } : {}),
    },
  }
}

/** Whether a message's origin is one of the configured host origins (exact match). */
export function isAllowedHostOrigin(origin: string, allowedOrigins: readonly string[]): boolean {
  return allowedOrigins.includes(origin)
}

/**
 * Whether the JWT's job matches the job the host has open. Ids are compared as numbers when both
 * are numeric, so `"0012"` and `"12"` match; true when the host sent no job.
 */
export function jobMatchesHost(jwtJobId: string | null, hostJobId: string | undefined): boolean {
  if (!hostJobId) return true
  if (!jwtJobId) return false

  const jwtNumber = Number(jwtJobId)
  const hostNumber = Number(hostJobId)

  if (Number.isFinite(jwtNumber) && Number.isFinite(hostNumber)) return jwtNumber === hostNumber

  return jwtJobId === hostJobId
}

export const larryReadyMessage = (): LarryToHostMessage => ({
  source: LARRY_EDITOR_SOURCE,
  action: 'ready',
})

export const larryCloseMessage = (spreadId: string): LarryToHostMessage => ({
  source: LARRY_EDITOR_SOURCE,
  action: 'close',
  payload: { spreadId },
})

export const larryErrorMessage = (code: EmbedErrorCode, message: string): LarryToHostMessage => ({
  source: LARRY_EDITOR_SOURCE,
  action: 'error',
  payload: { code, message },
})

export const larryActivityMessage = (): LarryToHostMessage => ({
  source: LARRY_EDITOR_SOURCE,
  action: 'activity',
})

export const larrySessionExpiredMessage = (): LarryToHostMessage => ({
  source: LARRY_EDITOR_SOURCE,
  action: 'session-expired',
})
